import React, { useEffect, useRef, useState } from "react";
import { color } from "../../styles";
import { TextBoxWrapper } from "./Styled";

interface TextBoxProps {
    text: string;
    onChangeText?: (value: string) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    disabled?: boolean;
    style?: React.CSSProperties;
    numeric?: boolean;
    placeholder?: string;
}

const EN_TYPING_REGEX = /^-?\d*(\.\d*)?$/;
const TYPING_REGEX = EN_TYPING_REGEX;

// This is for EN format
const normalizeTyping = (raw: string) => {
    let s = raw.replace(/[^\d.,\-]/g, "");

    // keep only one leading "-"
    s = (s.startsWith("-") ? "-" : "") + s.replace(/-/g, "");

    const unsigned = s.startsWith("-") ? s.slice(1) : s;
    const [intPart, decPart = ""] = unsigned.split(".");
    if (decPart.includes(",")) return s;
    if (intPart.includes(",") && !/^\d{1,3}(,\d{3})*$/.test(intPart)) return s;

    // remove thousand separators while typing
    s = s.replace(/,/g, "");

    // Reject multiple decimal dots.
    const parts = s.split(".");
    if (parts.length > 2) {
        return s;
    }

    return s;
};

const parseEnToNumber = (raw: string): number => {
    if (!raw.trim()) return NaN;
    const normalized = raw.replace(/,/g, "");
    const n = Number(normalized);
    return Number.isFinite(n) ? n : NaN;
};

const formatEn = (raw: string): string => {
    if (!raw.trim()) return "";
    const [, dec = ""] = raw.replace(/,/g, "").split(".");
    const n = parseEnToNumber(raw);
    if (!Number.isFinite(n)) return raw;
    return new Intl.NumberFormat("en-US", {
        useGrouping: true,
        minimumFractionDigits: dec.length,
        maximumFractionDigits: dec.length,
    }).format(n);
};

const TextBox = ({ text, onChangeText, onFocus, onBlur, disabled, style, numeric, placeholder }: TextBoxProps) => {
    const [inputValue, setInputValue] = useState(text ?? "");
    const [isValid, setIsValid] = useState(true);
    const [focused, setFocused] = useState(false);
    const prevPropTextRef = useRef<string | undefined>(undefined);

    // Sync from prop when it changes externally (but don't fight the user while typing)
    useEffect(() => {
        if (focused) return;
        if (text !== prevPropTextRef.current) {
            if (numeric) {
                const looksNumberish = /^\s*-?[\d,]+(\.\d+)?\s*$/.test(text ?? "");
                setInputValue(looksNumberish ? formatEn(text ?? "") : (text ?? ""));
            } else {
                setInputValue(text ?? "");
            }
            prevPropTextRef.current = text;
        }
    }, [text, numeric, focused]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;

        if (!numeric) {
            setInputValue(value);
            onChangeText?.(value);
            return;
        }

        value = normalizeTyping(value);

        const isOk = TYPING_REGEX.test(value) || value === "" || value === "-";
        setIsValid(isOk);

        if (!isOk) return;

        setInputValue(value);
        onChangeText?.(value);
    };

    const handleFocus = () => {
        setFocused(true);
        onFocus?.();
        if (!numeric) return;
        const ungrouped = inputValue.replace(/,/g, "");
        setInputValue(ungrouped);
    };

    const handleBlur = () => {
        setFocused(false);
        onBlur?.();
        if (!numeric) return;
        if (!inputValue.trim() || inputValue === "-") return;

        if (TYPING_REGEX.test(inputValue)) {
            const n = parseEnToNumber(inputValue);
            if (Number.isFinite(n)) {
                const pretty = formatEn(inputValue);
                setInputValue(pretty);
                onChangeText?.(pretty);
            }
        }
    };

    return (
        <TextBoxWrapper
            style={{
                ...style,
                borderColor: numeric && !isValid
                    ? "red"
                    : style?.borderColor || color.secondary.BORDER,
            }}
            value={inputValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={placeholder}
            inputMode={numeric ? "decimal" : undefined}
        />
    );
};

export default TextBox;
